import styles from "./stepper.module.css";
import Confirmation from "./Steps/Confirmation/Confirmation";
import Filial from "./Steps/Filial/Filial";
import Propietario from "./Steps/Owner/Owner";
import Autorizados from "./Steps/Authorized/Authorized";
import { useResidents } from "../../hooks/useResidents";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  validateFilial,
  validatePropietario,
} from "../../services/validationRules";

const STEPS = [
  { numStep: 0, title: "Filial", component: Filial },
  { numStep: 1, title: "Propietario", component: Propietario },
  { numStep: 2, title: "Autorizados", component: Autorizados },
  { numStep: 3, title: "Confirmación", component: Confirmation },
];

const INITIAL_FORM = {
  filial: {
    idFilial: "",
    numPlano: "",
    numFolio: "",
    tipoPropiedad: "",
  },
  propietario: {
    cedula: "",
    nombre: "",
    apellidos: "",
    nombre_sociedad: "",
    telefono_casa: "",
    telefono_cel: "",
    telefono_trabajo: "",
    placaVehiculo: "",
    placaExtranjera: false,
    notas: "",
    emailPrincipal: "",
    emailExtras: [],
  },
  copropietarios: [],
  autorizados: [],
};

export default function Stepper() {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const { createResident } = useResidents();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({}); // errores del paso actual

  const progressRef = useRef(null);

  useEffect(() => {
    if (!progressRef.current) return;
    const width = activeStep / (STEPS.length - 1);
    progressRef.current.style.transform = `scaleX(${width})`;
  }, [activeStep]);

  // ── Validar el paso actual ────────────────────────────────────────────────
  function validateStep(step) {
    if (step === 0) return validateFilial(formData.filial);
    if (step === 1) return validatePropietario(formData.propietario);
    return {}; // paso 2 (autorizados) es opcional, paso 3 es confirmación
  }

  function handleNext(e) {
    e.preventDefault();
    const errs = validateStep(activeStep);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Scroll hacia el primer error
      setTimeout(() => {
        const el = document.querySelector("[data-has-error='true']");
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }
    setErrors({});
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function handlePrev(e) {
    e.preventDefault();
    setErrors({});
    setActiveStep((s) => Math.max(s - 1, 0));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isLoading) return;
    setErrors({});
    try {
      setLoading(true);
      await createResident(formData);
      navigate("/residentes");
    } catch (err) {
      console.error("Fallo la secuencia de creación:", err);
      setErrors({ _global: err.message || 'Error al guardar. Revisá los datos e intentá de nuevo.' });
    } finally {
      setLoading(false);
    }
  }

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <>
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>Inicio </span> /
        </Link>
        <Link to="/residentes">
          <span className={styles.inactive}> Residentes </span>/
        </Link>
        <span className={styles.active}> Agregar</span>
      </span>

      <div className={styles.stepper} style={{ marginTop: "10px" }}>
        <form className={styles["form-wizard"]}>
          <h1 className="title">Registración</h1>

          {/* Barra de progreso */}
          <div className={styles["progress-container"]}>
            <div ref={progressRef} className={styles.progress} />
            <ol>
              {STEPS.map((step) => (
                <li
                  key={step.title}
                  className={
                    activeStep === step.numStep
                      ? styles.active
                      : activeStep > step.numStep
                        ? styles.done
                        : ""
                  }
                >
                  <span>{step.title}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Banner de errores globales del paso */}
          {hasErrors && (
            <div
              style={{
                background: "rgba(239,68,68,0.08)",
                border: "1px solid rgba(239,68,68,0.35)",
                borderLeft: "4px solid #ef4444",
                borderRadius: 8,
                padding: "10px 16px",
                margin: "0 0 12px",
                color: "#f87171",
                fontSize: "0.83rem",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="fa-solid fa-triangle-exclamation" />
              Hay campos con errores. Revísalos antes de continuar.
            </div>
          )}

          {/* Step actual */}
          <div className={styles["steps-container"]}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.35 }}
                style={{ width: "100%" }}
              >
                {(() => {
                  const Component = STEPS[activeStep].component;
                  return (
                    <Component
                      formData={formData}
                      setFormData={setFormData}
                      errors={errors}
                    />
                  );
                })()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Controles */}
          <div className={styles.controls}>
            <button
              type="button"
              className={styles.btn}
              onClick={handlePrev}
              disabled={activeStep === 0}
            >
              Atrás
            </button>

            {activeStep < STEPS.length - 1 ? (
              <button
                type="button"
                className={styles.btn}
                onClick={handleNext}
                style={{
                  background: hasErrors ? "rgba(239,68,68,0.15)" : undefined,
                  borderColor: hasErrors ? "rgba(239,68,68,0.5)" : undefined,
                }}
              >
                {hasErrors ? (
                  <>
                    <i
                      className="fa-solid fa-circle-exclamation"
                      style={{ marginRight: 5 }}
                    />
                    Corregir errores
                  </>
                ) : (
                  "Siguiente →"
                )}
              </button>
            ) : (
              <button
                type="submit"
                className={styles.btn}
                disabled={isLoading}
                onClick={handleSubmit}
              >
                {isLoading ? (
                  <>
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: 5 }}
                    />
                    Guardando…
                  </>
                ) : (
                  "Guardar Residente"
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
}
